import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../Dropdown/LocationName'; // Dropdown component for selecting locations

// AreaModal component for adding or editing area details in a modal
const AreaModal = ({
  formData, // Object containing form input data
  onChange, // Function to handle form input changes
  onSave, // Function to save form data
  editId, // ID to determine if modal is in edit mode (null for add mode)
}) => {
  // State to track whether form fields have been touched for validation
  const [touchedFields, setTouchedFields] = useState({
    location_id: false,
    area_name: false,
    longitude: false,
    latitude: false,
  });

  // State for form validation errors
  const [errors, setErrors] = useState({
    location_id: '',
    area_name: '',
    longitude: '',
    latitude: '',
  });

  // Reset validation when modal opens (Add or Edit)
  useEffect(() => {
    const modal = document.getElementById('bs-area-modal'); // Reference to modal element

    // Handle modal open event to reset touched fields and errors
    const handleModalOpen = () => {
      setTouchedFields({
        location_id: false,
        area_name: false,
        longitude: false,
        latitude: false,
      });
      setErrors({
        location_id: '',
        area_name: '',
        longitude: '',
        latitude: '',
      });
    };

    modal.addEventListener('show.bs.modal', handleModalOpen); // Add event listener for modal show

    // Cleanup event listener on component unmount
    return () => {
      modal.removeEventListener('show.bs.modal', handleModalOpen);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  /**
   * Handles form input changes with validation for coordinate fields
   * @param {Object} e - The event object
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for coordinate fields
    if (name === 'longitude' || name === 'latitude') {
      // Allow numbers, decimal point, and negative sign (valid coordinate format)
      if (/^-?\d*\.?\d*$/.test(value) || value === '') {
        onChange(e); // Update formData only if valid
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} must contain only numbers, a decimal point, or a negative sign`,
        }));
        return;
      }
    } else {
      // For location_id and area_name, pass through without validation
      onChange(e);
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Handles form submission with validation
   * @param {Object} e - The event object
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    // Mark all fields as touched when submitting
    setTouchedFields({
      location_id: true,
      area_name: true,
      longitude: true,
      latitude: true,
    });

    // Validate form fields
    const newErrors = {};
    let isValid = true;

    if (!formData.location_id?.toString().trim()) {
      newErrors.location_id = 'Location is required';
      isValid = false;
    }
    if (!formData.area_name?.trim()) {
      newErrors.area_name = 'Area name is required';
      isValid = false;
    }
    if (!formData.latitude?.trim()) {
      newErrors.latitude = 'Latitude is required';
      isValid = false;
    } else if (!/^-?\d*\.?\d*$/.test(formData.latitude)) {
      newErrors.latitude = 'Latitude must contain only numbers, a decimal point, or a negative sign';
      isValid = false;
    }
    if (!formData.longitude?.trim()) {
      newErrors.longitude = 'Longitude is required';
      isValid = false;
    } else if (!/^-?\d*\.?\d*$/.test(formData.longitude)) {
      newErrors.longitude = 'Longitude must contain only numbers, a decimal point, or a negative sign';
      isValid = false;
    }

    setErrors(newErrors);

    // Proceed with save if all fields are valid
    if (isValid) {
      onSave();
    }
  };

  /**
   * Marks a field as touched when it loses focus
   * @param {string} fieldName - The name of the field
   */
  const handleBlur = (fieldName) => {
    if (!touchedFields[fieldName]) {
      setTouchedFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  /**
   * Determines if validation message should be shown for a field
   * @param {string} fieldName - The name of the field to check
   * @returns {boolean} True if validation should be shown
   */
  const shouldShowValidation = (fieldName) => {
    return touchedFields[fieldName] && (errors[fieldName] || !formData[fieldName]?.toString().trim());
  };

  // Render modal
  return (
    <div
      className="modal fade"
      id="bs-area-modal"
      tabIndex="-1"
      aria-labelledby="bs-area-modal-label"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">{editId ? 'Edit Area' : 'Add Area'}</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              id="close-area-modal"
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              {/* Location Dropdown */}
              <div className="mb-3">
                <label htmlFor="locationId" className="form-label fw-semibold">
                  Location <span className="text-danger">*</span>
                </label>
                <LocationDropdown
                  id="locationId"
                  name="location_id"
                  className={`form-control ${shouldShowValidation('location_id') ? 'is-invalid' : ''}`}
                  value={formData.location_id || ''}
                  onChange={handleChange}
                  onBlur={() => handleBlur('location_id')}
                  required
                />
                {shouldShowValidation('location_id') && (
                  <div className="invalid-feedback">{errors.location_id || 'Location is required'}</div>
                )}
              </div>

              {/* Area Name Input */}
              <div className="mb-3">
                <label htmlFor="areaName" className="form-label fw-semibold">
                  Area Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="areaName"
                  name="area_name"
                  className={`form-control ${shouldShowValidation('area_name') ? 'is-invalid' : ''}`}
                  placeholder="Enter area name"
                  value={formData.area_name || ''}
                  onChange={handleChange}
                  onBlur={() => handleBlur('area_name')}
                  required
                />
                {shouldShowValidation('area_name') && (
                  <div className="invalid-feedback">{errors.area_name || 'Area name is required'}</div>
                )}
              </div>

              {/* Latitude Input */}
              <div className="mb-3">
                <label htmlFor="latitude" className="form-label fw-semibold">
                  Latitude <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="latitude"
                  name="latitude"
                  className={`form-control ${shouldShowValidation('latitude') ? 'is-invalid' : ''}`}
                  placeholder="Enter latitude (e.g., 12.345 or -12.345)"
                  value={formData.latitude || ''}
                  onChange={handleChange}
                  onBlur={() => handleBlur('latitude')}
                  required
                />
                <small className="form-text text-muted">
                  Only numbers, a decimal point, and a negative sign are allowed.
                </small>
                {shouldShowValidation('latitude') && (
                  <div className="invalid-feedback">{errors.latitude || 'Latitude is required'}</div>
                )}
              </div>

              {/* Longitude Input */}
              <div className="mb-3">
                <label htmlFor="longitude" className="form-label fw-semibold">
                  Longitude <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="longitude"
                  name="longitude"
                  className={`form-control ${shouldShowValidation('longitude') ? 'is-invalid' : ''}`}
                  placeholder="Enter longitude (e.g., 12.345 or -12.345)"
                  value={formData.longitude || ''}
                  onChange={handleChange}
                  onBlur={() => handleBlur('longitude')}
                  required
                />
                <small className="form-text text-muted">
                  Only numbers, a decimal point, and a negative sign are allowed.
                </small>
                {shouldShowValidation('longitude') && (
                  <div className="invalid-feedback">{errors.longitude || 'Longitude is required'}</div>
                )}
              </div>
            </form>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {editId ? 'Update Area' : 'Save Area'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the component
export default AreaModal;